import BaseEntity from 'client/components/core/BaseEntity/BaseEntityNew'
import EntityExampleWrapper from 'client/components/core/EntityExampleWrapper'
import { pricePlansStub } from 'client/constants/editorSettings'
import { useIsShowAdditionalAction } from 'client/hooks/use-is-show-additional-action'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import usePayment from 'client/hooks/usePayment'
import { getIsFetching, getPricePlans } from 'client/reducers/paymentReducer'
import { usePage, usePayment as useTypedPayment } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import typedPaymentSelectors from 'client/store/payment/paymentSelectors'
import { EntityProps } from 'client/types'
import translatePricePlans from 'client/utils/translatePriceStub'
import CommonOfferPrice from 'common/components/entities/OfferPriceNew'
import { OfferPriceInterface } from 'common/types/entities/OfferPriceInterface'
import { PricePlan } from 'common/types/OfferInterface'
import React, { useEffect, useState } from 'react'
import { calculatePrice } from 'src/editor/api/calculate-price'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import {
  CalculatedOfferPrice,
  CalculatePriceRequest,
} from 'src/shared/types/pricing-types'

const OfferPrice = ({
  entity,
  isMoveUpAllowed,
  isMoveDownAllowed,
}: EntityProps<OfferPriceInterface>) => {
  const isFetching = usePayment(getIsFetching)
  const pricePlans: PricePlan[] = usePayment(getPricePlans)
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const { isTemplate } = usePageRouteDetector()
  const entityParents = usePage(page =>
    pageSelectors.getEntityParents(page.entities, entity),
  )
  const product = useTypedPayment(typedPaymentSelectors.getProduct)
  const offer = useTypedPayment(typedPaymentSelectors.getOffer)
  const { i18n } = useTypedTranslation()
  const fixedT = i18n.getFixedT(pageLocale, ['client', 'common'])

  const [calculatedOfferPrice, setCalculatedOfferPrice] =
    useState<CalculatedOfferPrice>([])

  useEffect(() => {
    if (!offer.id || (!product?.id && !pricePlans.length)) {
      return
    }

    const fetchPriceData = async () => {
      try {
        const offers: CalculatePriceRequest[] = pricePlans.map(pricePlan => ({
          customer: {},
          quantity: 1,
          offer: offer.id,
          pricePlan: pricePlan.id,
        }))

        if (product?.id) {
          offers.push({
            customer: {},
            product: product?.id,
            pricePlan: product.pricePlan.id,
            quantity: 1,
            offer: offer.id,
          })
        }

        const { data } = await calculatePrice(offers)
        setCalculatedOfferPrice(data)
      } catch (_err) {
        // do nothing
      }
    }

    fetchPriceData()
  }, [offer?.id, product?.id, pricePlans])

  const isShowAdditionalAction = useIsShowAdditionalAction({
    entityParents,
    entity,
  })

  if (isFetching && !isTemplate) {
    return null
  }

  const isExample = pricePlans.length === 0 && !product
  const calculatedPricePlans: PricePlan[] = isExample
    ? translatePricePlans(pricePlansStub, fixedT)
    : pricePlans

  const mainOfferPricing = calculatedOfferPrice?.find(
    ({ pricePlan }) => product?.pricePlan?.id === pricePlan,
  )

  const pricePlansPricing = calculatedOfferPrice?.filter(({ pricePlan }) =>
    calculatedPricePlans.find(({ id }) => id === pricePlan),
  )

  // @TODO add more detailed example, only basic price is displayed in client atm
  const finalAmount = 0

  const renderOfferPrice = (entity: OfferPriceInterface) => {
    return (
      <CommonOfferPrice
        attrId={entity.htmlAttrId}
        mainOfferPricing={mainOfferPricing}
        pricePlansPricing={pricePlansPricing}
        plans={calculatedPricePlans}
        activePlanId={calculatedPricePlans[0]?.id}
        isVatNotChargeable={false}
        customDescription={entity.customDescription}
        product={product}
        finalAmount={finalAmount}
        togglePricePlan={() => null}
        locale={pageLocale}
        nameFontSize={entity.nameFontSize}
        mobileNameFontSize={entity.mobileNameFontSize}
        nameFontStyle={entity.nameFontStyle}
        mobileNameFontStyle={entity.mobileNameFontStyle}
        nameFontWeight={entity.nameFontWeight}
        mobileNameFontWeight={entity.mobileNameFontWeight}
        nameFontFamily={entity.nameFontFamily}
        mobileNameFontFamily={entity.mobileNameFontFamily}
        nameLineHeight={entity.nameLineHeight}
        mobileNameLineHeight={entity.mobileNameLineHeight}
        nameColor={entity.nameColor}
        mobileNameColor={entity.mobileNameColor}
        amountFontSize={entity.amountFontSize}
        mobileAmountFontSize={entity.mobileAmountFontSize}
        amountFontStyle={entity.amountFontStyle}
        mobileAmountFontStyle={entity.mobileAmountFontStyle}
        amountFontWeight={entity.amountFontWeight}
        mobileAmountFontWeight={entity.mobileAmountFontWeight}
        amountFontFamily={entity.amountFontFamily}
        mobileAmountFontFamily={entity.mobileAmountFontFamily}
        amountLineHeight={entity.amountLineHeight}
        mobileAmountLineHeight={entity.mobileAmountLineHeight}
        amountColor={entity.amountColor}
        mobileAmountColor={entity.mobileAmountColor}
        descriptionFontSize={entity.descriptionFontSize}
        mobileDescriptionFontSize={entity.mobileDescriptionFontSize}
        descriptionFontStyle={entity.descriptionFontStyle}
        mobileDescriptionFontStyle={entity.mobileDescriptionFontStyle}
        descriptionFontWeight={entity.descriptionFontWeight}
        mobileDescriptionFontWeight={entity.mobileDescriptionFontWeight}
        descriptionFontFamily={entity.descriptionFontFamily}
        mobileDescriptionFontFamily={entity.mobileDescriptionFontFamily}
        descriptionLineHeight={entity.descriptionLineHeight}
        mobileDescriptionLineHeight={entity.mobileDescriptionLineHeight}
        descriptionColor={entity.descriptionColor}
        mobileDescriptionColor={entity.mobileDescriptionColor}
      />
    )
  }

  return (
    <BaseEntity
      entity={entity}
      isMoveUpAllowed={isMoveUpAllowed}
      isMoveDownAllowed={isMoveDownAllowed}
      isSaveAllowed={isShowAdditionalAction}
      isRemoveAllowed={isShowAdditionalAction}
      isCopyAllowed={isShowAdditionalAction}
    >
      {isExample ? (
        <EntityExampleWrapper label="entities.offer_price.example_warning">
          {renderOfferPrice(entity)}
        </EntityExampleWrapper>
      ) : (
        renderOfferPrice(entity)
      )}
    </BaseEntity>
  )
}

export default OfferPrice
